import { useCallback, useMemo, useRef, useState } from "react"
import { roundTime } from "./helper"
import NodeComponent from "../../NodeComponent/NodeComponent"
import {
  DOT_SHAPE,
  LineWithBinaryStates_CHART_CATEGORY,
  LineWithControlRangeAndMean_CHART_CATEGORY,
  Line_CHART_CATEGORY,
  NONE
} from "src/blitz/Charts/constants"
import {
  reduxStoreTimeStampFormat,
  tooltipTimeStampFormat
} from "src/components/shared/Charts/constant"
import RBush from "rbush"
import { debounce, isFunction, isNumber, sortBy } from "lodash"
import Tooltip from "./Tooltip/Tooltip"
import {
  findNearest,
  generateValueY
} from "src/blitz/Charts/LineWithBinaryState/helper"
import {
  ANALYTIC_RESULT_LINE,
  LINE_TYPE,
  MEAN_LINE_TYPE
} from "src/components/shared/Charts/Helper/ChartCategories/lineChart"
import { getDateFormatByLocale } from "src/utils/CommonMethods"
import { useSelector } from "react-redux"
import { selectLanguage } from "src/redux/slicers/userData"

// Supported tooltip intervals ordered from finest to coarsest.
const INTERVAL_FALLBACK_ORDER = ["1m", "5m", "15m"] as const

const TimeseriesMousePointer = (props: any) => {
  const {
    chartCategory = "",
    xScale,
    x,
    width,
    height,
    mgWidth,
    mgHeight,
    yScaleRangeMax,
    timeLineHeight,
    data = {},
    legends = [],
    interval = "15m",
    systemOfMeasure
  } = props

  const svgRef = useRef()
  const rectRef = useRef()
  const lDateTimeFormat = getDateFormatByLocale("MM/DD/YY hh:mm A")
  const [timestamp, setTimestamp] = useState(null)
  const [position, setPosition] = useState(null)

  const [nearestYPosition, setNearestYPosition] = useState(null)

  const [rectDimensions, setRectDimensions]: any = useState({})
  const selectedLanguage = useSelector(selectLanguage) || "en"

  const getNodeCy = (obj, i, index, formatted) => {
    let nodeCY = ""
    if (
      chartCategory === Line_CHART_CATEGORY ||
      chartCategory === LineWithControlRangeAndMean_CHART_CATEGORY
    ) {
      const value =
        obj?.type === LINE_TYPE || obj?.type === MEAN_LINE_TYPE
          ? data?.[obj?.id]?.data?.[index]
          : obj?.type === ANALYTIC_RESULT_LINE
          ? obj?.value?.[`${formatted?.split("T")?.[0]}T00:00:00`]
          : obj?.value
      nodeCY = isFunction(obj?.yScale) ? obj?.yScale(value) : ""
    } else if (chartCategory === LineWithBinaryStates_CHART_CATEGORY) {
      const yPosition = i * 2 + 1
      nodeCY = isFunction(obj?.yScale)
        ? obj?.yScale(generateValueY(yPosition, data?.[obj?.id]?.data?.[index]))
        : ""
    }
    return nodeCY
  }
  const localeLanguage = ["ar"]

  // Helper to check if data exists for a given timestamp
  const hasDataForTimestamp = useCallback(
    (formatted: string) => {
      return legends.some((legend) => {
        if (legend?.type === LINE_TYPE || legend?.type === MEAN_LINE_TYPE) {
          const index = data?.[legend?.id]?.timestamps?.indexOf(formatted)
          return (
            index !== -1 &&
            index !== undefined &&
            isNumber(data?.[legend?.id]?.data?.[index])
          )
        } else if (legend?.type === ANALYTIC_RESULT_LINE) {
          const value =
            legend?.value?.[`${formatted?.split("T")?.[0]}T00:00:00`]
          return value !== undefined && isNumber(value)
        }
        return isNumber(legend?.value)
      })
    },
    [data, legends]
  )

  // Find timestamp with data using interval fallback (1m -> 5m -> 15m)
  const findTimestampWithData = useCallback(
    (xVal: any, initialInterval: string) => {
      // Respect the chart interval first, then only fall back to coarser buckets.
      // This keeps a 15m chart from snapping the tooltip to 1m or 5m data.
      const intervalIndex = INTERVAL_FALLBACK_ORDER.indexOf(
        initialInterval as (typeof INTERVAL_FALLBACK_ORDER)[number]
      )
      const intervalsToTry =
        intervalIndex >= 0
          ? INTERVAL_FALLBACK_ORDER.slice(intervalIndex)
          : [initialInterval, ...INTERVAL_FALLBACK_ORDER]

      for (const tryInterval of intervalsToTry) {
        const timestamp = localeLanguage.includes(selectedLanguage)
          ? roundTime(xVal, tryInterval).locale("en")
          : roundTime(xVal, tryInterval)
        const formatted = timestamp.format("YYYY-MM-DDTHH:mm:ss")

        if (hasDataForTimestamp(formatted)) {
          return { timestamp, formatted }
        }
      }

      // Return the initial interval's timestamp if no data found in fallback order
      const timestamp = localeLanguage.includes(selectedLanguage)
        ? roundTime(xVal, initialInterval).locale("en")
        : roundTime(xVal, initialInterval)

      return {
        timestamp,
        formatted: timestamp.format("YYYY-MM-DDTHH:mm:ss")
      }
    },
    [localeLanguage, selectedLanguage, hasDataForTimestamp]
  )

  const handleMouseMove = useCallback(
    (event, xScale, timestamp, rectRef) => {
      if (rectRef?.current) {
        const rectDimensions = rectRef.current.getBoundingClientRect()

        const { clientX, clientY } = event
        const rectX = Math.floor(rectDimensions?.x)
        const rectY = Math.ceil(rectDimensions?.y)

        const isMouseOverRect =
          clientX >= rectX &&
          clientX <= +rectX + +mgWidth &&
          clientY >= rectY &&
          clientY <= +rectY + +yScaleRangeMax

        if (isMouseOverRect) {
          const rbush = new RBush()
          const bufferWidthVal = 4
          const adjustedY = clientY - rectY
          const adjustedX = clientX - rectX
          const xVal = xScale.invert(adjustedX)

          // Use fallback logic: try current interval, then fallback to larger intervals if no data
          const { timestamp: nearestTimestamp, formatted } =
            findTimestampWithData(xVal, interval)

          const currentPosition = xScale(new Date(formatted))
          const cy = legends
            .map((obj, i) => {
              const index = data?.[obj?.id]?.timestamps?.indexOf(formatted)
              return getNodeCy(obj, i, index, formatted)
            })
            .map((item) => ({
              minX: currentPosition - bufferWidthVal,
              minY: item - bufferWidthVal,
              maxX: currentPosition + bufferWidthVal,
              maxY: item + bufferWidthVal,
              foo: "bar"
            }))
          rbush.load(cy)
          const b = rbush.search({
            minX: adjustedX - bufferWidthVal * 3,
            minY: adjustedY - bufferWidthVal * 3,
            maxX: adjustedX + bufferWidthVal * 3,
            maxY: adjustedY + bufferWidthVal * 3
          })
          if (b.length > 0) {
            setNearestYPosition(b[0].minY + 2)
          } else {
            setNearestYPosition(null)
          }
          setPosition(currentPosition)
          setTimestamp(nearestTimestamp)
          setRectDimensions(rectDimensions)
        } else {
          setPosition(null)
          setTimestamp(null)
        }
      }
    },
    [yScaleRangeMax, mgWidth, findTimestampWithData, interval]
  )

  const debouncedHandleMouseMove = useCallback(
    debounce(
      (e, xScale, timestamp, rectRef) =>
        handleMouseMove(e, xScale, timestamp, rectRef),
      1
    ),
    [handleMouseMove]
  )
  const handleMouseLeave = useCallback(() => {
    setTimestamp(null)
    setPosition(null)
  }, [])
  const debouncedHandleMouseLeave = useCallback(
    debounce(() => handleMouseLeave(), 1),
    [handleMouseLeave]
  )

  const { nodeCX, formattedDate, tooltipVisible, toolTipProps, dataArray } =
    useMemo(() => {
      const rectX = Math.floor(rectDimensions?.x)
      const formattedDate = timestamp?.format(reduxStoreTimeStampFormat)
      const nodeCX = timestamp ? xScale(new Date(formattedDate)) : 0
      let title1 = ""
      if (typeof lDateTimeFormat === "function" && timestamp) {
        // For Arabic, use the function returned by getDateFormatByLocale
        title1 = lDateTimeFormat(
          timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
        )
      } else if (timestamp) {
        // For other languages, use moment's format string
        title1 = timestamp.format(lDateTimeFormat)
      }
      const chartwidthHalf = mgWidth / 2

      const positionObj: any = {
        bottom: timeLineHeight > 0 ? timeLineHeight + 70 : 40
      }

      if (position <= chartwidthHalf) {
        positionObj.left = position + rectX
      } else {
        positionObj.left = position + 40 - 420
      }

      const dimensionsObj = {
        maxHeight: 400,
        maxWidth: 420,
        minWidth: 420
      }
      const toolTipProps = {
        title1,
        positionObj,
        dimensionsObj,
        backgroundColor: "black"
      }

      let dataArray = []
      const firstN = 15

      legends?.forEach((legend) => {
        let value: any = ""
        if (legend?.type === LINE_TYPE || legend?.type === MEAN_LINE_TYPE) {
          const index = data?.[legend?.id]?.timestamps?.indexOf(formattedDate)
          value = data?.[legend?.id]?.data?.[index]
        } else if (legend?.type === ANALYTIC_RESULT_LINE) {
          value = legend?.value?.[`${formattedDate?.split("T")?.[0]}T00:00:00`]
        } else {
          value = parseInt(legend?.value)
        }
        const isValueExistsInRange =
          legend?.type === LINE_TYPE && legend?.range?.length > 0
            ? value >= legend?.range[0] && value <= legend?.range[1]
              ? true
              : false
            : true
        if (chartCategory === "LineWithBinaryStates") {
          const legendObj = {
            color: legend?.color,
            shape: legend?.shape === NONE ? DOT_SHAPE : legend?.shape,
            name: legend?.name || "",
            value: value,
            ypos: isFunction(legend?.yScale) ? legend?.yScale(value) : "",
            symbol:
              legend?.symbolList?.[systemOfMeasure] || legend?.symbol || ""
          }
          dataArray.push(legendObj)
        } else if (
          isNumber(value) &&
          legend?.show &&
          legend?.visible &&
          isValueExistsInRange
        ) {
          const legendObj = {
            color: legend?.color,
            shape: legend?.shape === NONE ? DOT_SHAPE : legend?.shape,
            name: legend?.name || "",
            value: parseFloat(`${value}`),
            ypos: isFunction(legend?.yScale) ? legend?.yScale(value) : "",
            symbol:
              legend?.symbolList?.[systemOfMeasure] || legend?.symbol || "",
            isNearestPoint: false
          }
          dataArray.push(legendObj)
        }
      })

      if (chartCategory === "LineWithBinaryStates") {
        dataArray = dataArray.filter((d) => typeof d.value === "string")
        dataArray = sortBy(dataArray, "name")
      } else {
        if (nearestYPosition) {
          dataArray = dataArray.sort(
            (a, b) =>
              Math.abs(nearestYPosition - a.ypos) -
              Math.abs(nearestYPosition - b.ypos)
          )
        }
        dataArray = dataArray?.slice(0, firstN)
        dataArray = sortBy(dataArray, "name")
        if (dataArray.length > 0 && nearestYPosition) {
          const nearestPoint = findNearest(dataArray, nearestYPosition)
          dataArray = dataArray.map((arr) => {
            if (nearestPoint.ypos === arr.ypos) {
              arr.isNearestPoint = true
            }
            return arr
          })
        }
      }

      const tooltipVisible =
        (position === 0 || Boolean(position)) && dataArray?.length
          ? "visible"
          : "hidden"

      return {
        dataArray,
        toolTipProps,
        nodeCX,
        formattedDate,
        tooltipVisible
      }
    }, [position, rectDimensions, mgWidth, timeLineHeight, height, legends])

  return (
    <>
      <svg
        ref={svgRef}
        onMouseLeave={debouncedHandleMouseLeave}
        onMouseMove={(e) =>
          debouncedHandleMouseMove(e, xScale, timestamp, rectRef)
        }
        height="100%"
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      >
        <clipPath id="chart-area-clip">
          <rect x="0" y="0" width={mgWidth} height={mgHeight} />
        </clipPath>
        <g
          style={{
            transform: `translate(${50}px,${50}px)`
          }}
        >
          <clipPath id="chart-line-area-clip">
            <rect x="0" y="0" width={mgWidth} height={yScaleRangeMax} />
          </clipPath>
          <rect
            ref={rectRef}
            x={x}
            y={0}
            fill="transparent"
            width={mgWidth}
            height={yScaleRangeMax}
            className={"data-plot-area"}
          />
          <g>
            {Boolean(timestamp) && (
              <line
                y1={0}
                y2={yScaleRangeMax}
                x1={position}
                x2={position}
                stroke="white"
                opacity={0.6}
                strokeWidth={2}
              />
            )}
          </g>
          <g
            data-testid="line-chart-data"
            clipPath="url(#chart-line-area-clip)"
          >
            {legends?.map((obj, i) => {
              let nodeCY = ""
              const index = data?.[obj?.id]?.timestamps?.indexOf(formattedDate)
              if (
                chartCategory === Line_CHART_CATEGORY ||
                chartCategory === LineWithControlRangeAndMean_CHART_CATEGORY
              ) {
                const value =
                  obj?.type === LINE_TYPE || obj?.type === MEAN_LINE_TYPE
                    ? data?.[obj?.id]?.data?.[index]
                    : obj?.type === ANALYTIC_RESULT_LINE
                    ? obj?.value?.[`${formattedDate?.split("T")?.[0]}T00:00:00`]
                    : obj?.value
                nodeCY = isFunction(obj?.yScale) ? obj?.yScale(value) : ""
              } else if (
                chartCategory === LineWithBinaryStates_CHART_CATEGORY
              ) {
                const yPosition = i * 2 + 1
                nodeCY = isFunction(obj?.yScale)
                  ? obj?.yScale(
                      generateValueY(yPosition, data?.[obj?.id]?.data?.[index])
                    )
                  : ""
              }

              const visibility =
                isNumber(nodeCX) && isNumber(nodeCY) && obj?.show
                  ? "visible"
                  : "hidden"
              return (
                <g key={i} data-testid={`line-chart-data-group-${i}`}>
                  <NodeComponent
                    key={`${i}-${obj?.shape}`}
                    shape={obj?.shape}
                    size={obj?.shapeSize}
                    color={obj?.color}
                    cx={nodeCX}
                    cy={nodeCY}
                    visibility={visibility}
                  />
                </g>
              )
            })}
          </g>
          {props?.children}
        </g>
      </svg>
      <Tooltip
        key={`tooltip-${legends?.length}-${
          Object.keys(data || {}).length
        }-timestamps`}
        {...toolTipProps}
        data={dataArray}
        visibility={tooltipVisible}
      />
    </>
  )
}

export default TimeseriesMousePointer
